import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { Separator } from "@shared/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { 
  FileText, 
  Search, 
  Receipt, 
  Package, 
  AlertTriangle, 
  CheckCircle2,
  Eye,
  ExternalLink,
  Calendar,
  DollarSign,
  User,
  MapPin
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Link } from "react-router-dom";

const ReturnVoucherInvoiceCheck = ({ voucher }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  // Analyze packages and their invoice status
  const packageAnalysis = useMemo(() => {
    if (!voucher.packages) return { invoiced: [], uninvoiced: [], total: 0 };

    const invoicedPackages = voucher.packages.filter(pkg => pkg.invoiced || pkg.invoice);
    const uninvoicedPackages = voucher.packages.filter(pkg => !pkg.invoiced && !pkg.invoice);

    return {
      invoiced: invoicedPackages,
      uninvoiced: uninvoicedPackages,
      total: voucher.packages.length,
      invoicedAmount: invoicedPackages.reduce((sum, pkg) => sum + (pkg.totalAmount || 0), 0),
      uninvoicedAmount: uninvoicedPackages.reduce((sum, pkg) => sum + (pkg.totalAmount || 0), 0)
    };
  }, [voucher.packages]);

  // Filter packages based on search term
  const filteredPackages = useMemo(() => {
    if (!searchTerm) return voucher.packages || [];
    
    return (voucher.packages || []).filter(pkg => 
      pkg.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.customer?.phone?.includes(searchTerm) ||
      pkg.customer?.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [voucher.packages, searchTerm]);

  const PackageItem = ({ pkg, showInvoiceStatus = true }) => {
    const isInvoiced = pkg.invoiced || pkg.invoice;
    
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {pkg.trackingNumber || `PKG-${pkg._id?.slice(-6)}`}
            </Badge>
            {showInvoiceStatus && (
              <Badge 
                variant={isInvoiced ? "default" : "destructive"}
                className="text-xs"
              >
                {isInvoiced ? (
                  <CheckCircle2 className="size-3 mr-1" />
                ) : (
                  <AlertTriangle className="size-3 mr-1" />
                )}
                {isInvoiced ? t("invoices.invoiced") : t("invoices.pending")}
              </Badge>
            )}
          </div>
          
          <div className="text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <User className="size-3" />
              <span>{pkg.customer?.name}</span>
              <span className="text-gray-400">•</span>
              <span>{pkg.customer?.phone}</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="size-3" />
              <span>{pkg.customer?.city}</span>
              <span className="text-gray-400">•</span>
              <span className="font-medium">{pkg.totalAmount} MAD</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {pkg.invoice && (
            <Link to={`/invoices/${pkg.invoice}`}>
              <Button variant="ghost" size="sm">
                <Receipt className="size-4 mr-1" />
                {t("invoices.viewInvoice")}
                <ExternalLink className="size-3 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="size-5" />
            {t("returnVoucher.invoiceCheck")}
          </CardTitle>
          <Badge variant="outline" className="text-sm">
            {t("returnVoucher.voucherRef")}: {voucher._id?.slice(-8)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 text-center">
              <Package className="size-6 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold text-blue-700">{packageAnalysis.total}</div>
              <div className="text-sm text-gray-600">{t("common.totalPackages")}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="size-6 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold text-green-700">{packageAnalysis.invoiced.length}</div>
              <div className="text-sm text-gray-600">{t("invoices.invoiced")}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="size-6 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold text-orange-700">{packageAnalysis.uninvoiced.length}</div>
              <div className="text-sm text-gray-600">{t("invoices.pending")}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4 text-center">
              <DollarSign className="size-6 mx-auto mb-2 text-purple-600" />
              <div className="text-lg font-bold text-purple-700">{packageAnalysis.uninvoicedAmount} MAD</div>
              <div className="text-sm text-gray-600">{t("invoices.pendingAmount")}</div>
            </CardContent>
          </Card>
        </div>

        {/* Warning for uninvoiced packages */}
        {packageAnalysis.uninvoiced.length > 0 && (
          <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <AlertTriangle className="size-5 text-orange-600" />
            <div className="flex-1">
              <h4 className="font-medium text-orange-800">
                {t("invoices.uninvoicedWarning")}
              </h4>
              <p className="text-sm text-orange-700">
                {packageAnalysis.uninvoiced.length} {t("packages.packages")} {t("invoices.requireInvoicing")} ({packageAnalysis.uninvoicedAmount} MAD)
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
          <Input
            placeholder={t("common.searchPackages")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Package Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">
              {t("common.all")} ({packageAnalysis.total})
            </TabsTrigger>
            <TabsTrigger value="invoiced" className="text-green-700">
              {t("invoices.invoiced")} ({packageAnalysis.invoiced.length})
            </TabsTrigger>
            <TabsTrigger value="uninvoiced" className="text-orange-700">
              {t("invoices.pending")} ({packageAnalysis.uninvoiced.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <ScrollArea className="h-96 w-full">
              <div className="space-y-2">
                {filteredPackages.map((pkg) => (
                  <PackageItem key={pkg._id} pkg={pkg} />
                ))}
                {filteredPackages.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t("common.noResults")}
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="invoiced" className="mt-4">
            <ScrollArea className="h-96 w-full">
              <div className="space-y-2">
                {packageAnalysis.invoiced.filter(pkg => 
                  !searchTerm || 
                  pkg.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pkg.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pkg.customer?.phone?.includes(searchTerm) ||
                  pkg.customer?.city?.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((pkg) => (
                  <PackageItem key={pkg._id} pkg={pkg} showInvoiceStatus={false} />
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="uninvoiced" className="mt-4">
            <ScrollArea className="h-96 w-full">
              <div className="space-y-2">
                {packageAnalysis.uninvoiced.filter(pkg => 
                  !searchTerm || 
                  pkg.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pkg.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pkg.customer?.phone?.includes(searchTerm) ||
                  pkg.customer?.city?.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((pkg) => (
                  <PackageItem key={pkg._id} pkg={pkg} showInvoiceStatus={false} />
                ))}
                {packageAnalysis.uninvoiced.length === 0 && (
                  <div className="text-center py-8 text-green-500">
                    <CheckCircle2 className="size-12 mx-auto mb-3" />
                    <h3 className="font-medium">{t("invoices.allInvoiced")}</h3>
                    <p className="text-sm text-gray-500">{t("invoices.allPackagesInvoiced")}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Client and Voucher Information */}
        <Separator />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">{t("client.information")}</h4>
            <div className="space-y-1 text-gray-600">
              <div>{voucher.client?.brandName}</div>
              <div>{voucher.client?.phone}</div>
              <div>{voucher.client?.city}</div>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">{t("returnVoucher.information")}</h4>
            <div className="space-y-1 text-gray-600">
              <div>{t("common.created")}: {format(voucher.createdAt, "dd/MM/yyyy HH:mm")}</div>
              <div>{t("common.status")}: {t(`status.${voucher.status}`)}</div>
              <div>{t("common.total")}: {voucher.totalAmount} MAD</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReturnVoucherInvoiceCheck;